/**
 * WarningOverlay - Visual warning overlay for proctoring alerts
 *
 * Displays warnings at the top of the screen with:
 * - Slide-down/slide-up animations via Framer Motion
 * - Warning type-specific icons (face, multiple, tab, general)
 * - Manual dismiss button
 * - Red background for high visibility
 */

import { motion, AnimatePresence } from 'framer-motion'
import { AlertTriangle, Eye, Users, Monitor, Maximize, X, type LucideIcon } from 'lucide-react'
import { useWarningStore, type WarningType } from '../store/warningStore'
import { enterFullscreen } from '../utils/fullscreen'

// Map warning types to icons
const WARNING_ICONS: Record<Exclude<WarningType, null>, LucideIcon> = {
  face: Eye,
  multiple: Users,
  tab: Monitor,
  fullscreen: Maximize,
  general: AlertTriangle,
}

// Map warning types to accessible labels
const WARNING_LABELS: Record<Exclude<WarningType, null>, string> = {
  face: 'Face Detection Warning',
  multiple: 'Multiple Faces Warning',
  tab: 'Tab Switch Warning',
  fullscreen: 'Fullscreen Required',
  general: 'Warning',
}

export default function WarningOverlay() {
  const { activeWarning, warningType, dismiss } = useWarningStore()

  // Don't render anything if no warning
  if (!activeWarning) {
    return null
  }

  const Icon = warningType ? WARNING_ICONS[warningType] : AlertTriangle
  const typeLabel = warningType ? WARNING_LABELS[warningType] : 'Warning'

  return (
    <AnimatePresence mode="wait">
      <motion.div
        key={activeWarning}
        initial={{ y: -100, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: -100, opacity: 0 }}
        transition={{
          type: 'spring',
          stiffness: 300,
          damping: 30,
        }}
        className="fixed top-4 left-1/2 -translate-x-1/2 z-50 w-[90%] max-w-lg"
        role="alert"
        aria-live="assertive"
        aria-label={typeLabel}
      >
        <div className="bg-red-600 text-white rounded-lg shadow-xl px-4 py-3 flex items-center gap-3">
          {/* Warning Icon */}
          <div className="flex-shrink-0">
            <Icon
              className="w-6 h-6 text-white"
              aria-hidden="true"
            />
          </div>

          {/* Warning Message */}
          <p className="flex-grow text-sm font-medium leading-tight">
            {activeWarning}
          </p>

          {warningType === 'fullscreen' && (
            <button
              type="button"
              onClick={() => void enterFullscreen()}
              className="flex-shrink-0 px-3 py-1.5 rounded bg-white/20 hover:bg-white/30 text-xs font-semibold transition-colors"
            >
              Fullscreen
            </button>
          )}

          {/* Dismiss Button */}
          <button
            onClick={dismiss}
            className="flex-shrink-0 p-1 rounded hover:bg-red-700 transition-colors focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-red-600"
            aria-label="Dismiss warning"
          >
            <X className="w-5 h-5" aria-hidden="true" />
          </button>
        </div>
      </motion.div>
    </AnimatePresence>
  )
}
